import { useCallback, useEffect, useState } from "react";
import { IS_SERVER } from "@/constants";
export const useModal = (defaultValue, modalId) => {
    const [isOpen, setIsOpen] = useState(!!defaultValue);
    const [previousUrl, setPreviousUrl] = useState(null);
    const hasCorrectQuery = useCallback(() => {
        if (IS_SERVER)
            return false;
        const params = new URLSearchParams(window.location.search);
        return params.get("modal") === modalId;
    }, [modalId]);
    const open = useCallback(() => {
        if (IS_SERVER)
            return;
        // Store the current URL as the previous URL before opening the modal
        setPreviousUrl(window.location.href);
        setIsOpen(true);
        const params = new URLSearchParams(window.location.search);
        params.set("modal", modalId);
        const newUrl = `${window.location.pathname}?${params.toString()}`;
        if (window.location.href !== newUrl) {
            window.history.pushState({}, "", newUrl);
        }
    }, [modalId]);
    /**
     * @description Only used with masonry and slider modals
     */
    const openAndReplace = useCallback(() => {
        setIsOpen(true);
        const params = new URLSearchParams(window.location.search);
        params.set("modal", modalId);
        const newUrl = `${window.location.pathname}?${params.toString()}`;
        if (window.location.href !== newUrl) {
            window.history.replaceState({}, "", newUrl);
        }
    }, [modalId]);
    const closeAndGoBack = useCallback(() => {
        if (hasCorrectQuery() && !IS_SERVER) {
            setIsOpen(false);
            window.history.back();
        }
    }, [hasCorrectQuery]);
    /**
     * @description Only used with masonry and slider modals
     */
    const closeAndReplace = useCallback(() => {
        setIsOpen(false);
        const params = new URLSearchParams(window.location.search);
        params.delete("modal", modalId);
        const newUrl = window.location.pathname;
        if (window.location.href !== newUrl) {
            window.history.replaceState({}, "", newUrl);
        }
    }, [modalId]);
    const close = useCallback(() => {
        if (hasCorrectQuery() && !IS_SERVER) {
            if (history.length === 1 || !previousUrl) {
                closeAndReplace();
            }
            else {
                closeAndGoBack();
            }
        }
    }, [closeAndGoBack, closeAndReplace, hasCorrectQuery, previousUrl]);
    const toggle = useCallback(() => {
        if (hasCorrectQuery()) {
            close();
        }
        else {
            open();
        }
    }, [close, open, hasCorrectQuery]);
    useEffect(() => {
        if (defaultValue && !hasCorrectQuery()) {
            open();
        }
        if (hasCorrectQuery()) {
            setIsOpen(true);
        }
    }, [hasCorrectQuery, open, defaultValue]);
    useEffect(() => {
        const handlePopState = () => setIsOpen(hasCorrectQuery());
        window.addEventListener("popstate", handlePopState);
        return () => {
            window.removeEventListener("popstate", handlePopState);
        };
    }, [hasCorrectQuery]);
    return {
        isOpen,
        open,
        close,
        toggle,
        openAndReplace,
        closeAndReplace,
    };
};
